import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiAlertCircle, FiCheckCircle, FiLock, FiMail, FiUser, FiUserPlus, FiX } from 'react-icons/fi';
import authService from '../utils/auth.js';
import TrialToast from './TrialToast.jsx';
import AuthShell from './AuthShell.jsx';

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [trialToast, setTrialToast] = useState(null);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const formatTrialEndDate = (dateValue) => {
    if (!dateValue) return null;
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!validateEmail(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      const response = await authService.register(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );

      const trialEndsAtLabel = formatTrialEndDate(response?.trial?.trialEndsAt);
      if (response?.notifyTrialStarted && response?.trial?.isTrialUser && trialEndsAtLabel) {
        const detail = `Your trial includes full chat and admin dashboard access until ${trialEndsAtLabel}.`;
        setTrialToast({
          message: 'Your 3-day trial has started.',
          detail
        });
        setSuccess(`${response.message} ${detail}`);
      } else {
        setSuccess(response.message);
      }
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const mismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  return (
    <AuthShell>
      {trialToast && (
        <TrialToast
          message={trialToast.message}
          detail={trialToast.detail}
          onClose={() => setTrialToast(null)}
        />
      )}
      <div className="auth-card">
        <div className="auth-header">
          <span className="auth-icon" aria-hidden="true"><FiUserPlus /></span>
          <h1>Create your account</h1>
          <p>Sign up for NovaBotX™ and start your free trial</p>
        </div>

        {error && (
          <div className="auth-alert auth-alert-error" role="alert">
            <FiAlertCircle className="auth-alert-icon" aria-hidden="true" />
            <span className="auth-alert-text">{error}</span>
            <button type="button" onClick={() => setError('')} className="auth-alert-close" aria-label="Dismiss"><FiX /></button>
          </div>
        )}

        {success && (
          <div className="auth-alert auth-alert-success" role="status">
            <FiCheckCircle className="auth-alert-icon" aria-hidden="true" />
            <span className="auth-alert-text">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-row">
            <div className="auth-form-group">
              <label htmlFor="firstName">First name</label>
              <div className="auth-input">
                <FiUser aria-hidden="true" />
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                  placeholder="John"
                />
              </div>
            </div>
            <div className="auth-form-group">
              <label htmlFor="lastName">Last name</label>
              <div className="auth-input">
                <FiUser aria-hidden="true" />
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                  placeholder="Doe"
                />
              </div>
            </div>
          </div>
          <div className="auth-form-group">
            <label htmlFor="email">Work email</label>
            <div className="auth-input">
              <FiMail aria-hidden="true" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                placeholder="you@company.com"
              />
            </div>
          </div>
          <div className="auth-form-group">
            <label htmlFor="password">Password</label>
            <div className="auth-input">
              <FiLock aria-hidden="true" />
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                placeholder="At least 6 characters"
              />
            </div>
          </div>
          <div className="auth-form-group">
            <label htmlFor="confirmPassword">Confirm password</label>
            <div className={`auth-input${mismatch ? ' is-invalid' : ''}`}>
              <FiLock aria-hidden="true" />
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                aria-invalid={mismatch ? 'true' : undefined}
                placeholder="Re-enter your password"
              />
            </div>
            {mismatch && (
              <p className="auth-help-text auth-field-error"><FiAlertCircle aria-hidden="true" /> Passwords do not match</p>
            )}
          </div>
          <button type="submit" className="auth-button auth-button-primary" disabled={loading}>
            {loading && <span className="auth-spinner" aria-hidden="true" />}
            {loading ? 'Creating Account...' : 'Register'}
          </button>
          <div className="auth-links">
            <Link to="/login" className="auth-link">Already have an account? Sign in</Link>
          </div>
        </form>
      </div>
    </AuthShell>
  );
}

export default Register;
